// Galaxy-map panel — Phase 6 Block A.
//
// Simple HTML overlay (same pattern as hud-hints.ts) that lists all
// stations and lets the player request a jump. Toggled by G key.
// Client-facing only: sends requestJump; server validates and emits
// jumpStarted durable. The travel overlay replaces this panel on
// jumpStarted receipt.

import { balance } from "./types.ts";
import { buildRequestJumpMessage } from "../../shared/station-messages.ts";
import { STATIONS, STATION_IDS, type StationId } from "../../shared/stations.ts";

type GalaxyMapDeps = {
  readonly send: (msg: ReturnType<typeof buildRequestJumpMessage>) => void;
  readonly getCurrentStationId: () => string | null;
  readonly isTraveling: () => boolean;
};

export class GalaxyMapUI {
  private el: HTMLDivElement | null = null;
  private _isOpen = false;
  private deps: GalaxyMapDeps;

  constructor(deps: GalaxyMapDeps) {
    this.deps = deps;
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  open(): void {
    if (this._isOpen) return;
    this._isOpen = true;
    this.el = this.buildPanel();
    document.body.appendChild(this.el);
  }

  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.el?.remove();
    this.el = null;
  }

  toggle(): void {
    if (this._isOpen) this.close();
    else this.open();
  }

  private buildPanel(): HTMLDivElement {
    const panel = document.createElement("div");
    panel.style.cssText =
      "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);" +
      "background:#1a1a2e;border:1px solid #444;border-radius:6px;" +
      "padding:20px 28px;min-width:280px;font-family:monospace;color:#ccc;" +
      "z-index:9000;user-select:none";

    const title = document.createElement("div");
    title.textContent = "Galaxy Map";
    title.style.cssText = "font-size:15px;font-weight:bold;color:#eee;margin-bottom:14px";
    panel.appendChild(title);

    const currentId = this.deps.getCurrentStationId() ?? "station-7";
    const traveling = this.deps.isTraveling();

    for (const id of STATION_IDS) {
      const station = STATIONS[id];
      const row = document.createElement("div");
      row.style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;gap:16px";

      const label = document.createElement("div");
      const isCurrent = id === currentId;
      label.textContent = `${station.displayName}${isCurrent ? " ← you" : ""}`;
      label.style.cssText = `font-size:12px;color:${isCurrent ? "#88ff88" : "#aaa"}`;
      row.appendChild(label);

      if (!isCurrent) {
        const info = document.createElement("div");
        info.textContent = `${balance.travel.jumpCreditsCost}c · ${balance.travel.jumpDurationMs / 1000}s`;
        info.style.cssText = "font-size:11px;color:#777";
        row.appendChild(info);

        const btn = document.createElement("button");
        btn.textContent = traveling ? "Traveling…" : "Travel";
        btn.disabled = traveling;
        btn.style.cssText =
          "background:#2a4a7a;border:1px solid #446;color:#88bbff;padding:3px 10px;" +
          "border-radius:3px;cursor:pointer;font-family:monospace;font-size:11px";
        btn.addEventListener("click", () => {
          this.deps.send(buildRequestJumpMessage(id as StationId));
          this.close();
        });
        row.appendChild(btn);
      }

      panel.appendChild(row);
    }

    const hint = document.createElement("div");
    hint.textContent = `[${balance.input.galaxyMapKey.replace("Key", "")}] or [Esc] close`;
    hint.style.cssText = "font-size:10px;color:#555;margin-top:12px";
    panel.appendChild(hint);

    return panel;
  }
}

// Renders a travel overlay while a jump is in-flight.
export class TravelOverlay {
  private el: HTMLDivElement | null = null;

  show(targetName: string, completesAt: number): void {
    this.hide();
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,0.75);" +
      "display:flex;flex-direction:column;align-items:center;justify-content:center;" +
      "font-family:monospace;color:#ccc;z-index:8999";

    const title = document.createElement("div");
    title.textContent = `Traveling to ${targetName}…`;
    title.style.cssText = "font-size:20px;color:#88bbff;margin-bottom:12px";
    overlay.appendChild(title);

    const countdown = document.createElement("div");
    countdown.style.cssText = "font-size:14px;color:#aaa";
    overlay.appendChild(countdown);

    const tick = (): void => {
      const remaining = Math.max(0, Math.ceil((completesAt - Date.now()) / 1000)); // contracts:allow-literal
      countdown.textContent = `Arriving in ${remaining}s`;
      if (remaining > 0) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    this.el = overlay;
    document.body.appendChild(overlay);
  }

  hide(): void {
    this.el?.remove();
    this.el = null;
  }

  get isShowing(): boolean {
    return this.el !== null;
  }
}
